import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiDatabase, FiArrowRight, FiZap, FiSmile, 
  FiMessageSquare, FiCopy, FiTag, FiStar,
  FiCheckCircle, FiEdit3, FiBookOpen
} = FiIcons;

const LandingPage = () => {
  return (
    <div className="flex flex-col w-full overflow-hidden font-sans">
      
      {/* --- HERO SECTION --- */}
      <section className="relative bg-neo-bg pt-16 pb-20 sm:pt-20 sm:pb-24 px-4 border-b-4 border-black text-center">
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="inline-block bg-neo-yellow border-2 border-black px-4 py-1 mb-6 shadow-neo-sm transform -rotate-2">
            <span className="font-bold text-sm uppercase tracking-widest">For ChatGPT, Claude, & Gemini</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black uppercase text-black mb-6 leading-tight">
            Stop Typing The <br/>
            <span className="bg-black text-white px-4 transform rotate-1 inline-block mt-2">Same Thing</span> Twice.
          </h1>
          
          <p className="mt-6 text-xl md:text-2xl text-gray-700 font-medium max-w-2xl mx-auto leading-relaxed">
            Your personal library for AI prompts. Save your favorites, fill in the blanks, and copy them in seconds.
          </p>

          <div className="mt-10">
            <Link
              to="/auth"
              className="inline-flex items-center px-8 py-4 sm:px-10 sm:py-5 bg-neo-blue text-black border-2 border-black font-black text-lg sm:text-xl uppercase tracking-wider shadow-neo-lg hover:-translate-y-1 hover:shadow-neo-xl transition-all min-h-[52px]"
            >
              <span className="hidden sm:inline">Start for Free</span>
              <span className="sm:hidden">Start Free</span>
              <SafeIcon icon={FiArrowRight} className="ml-2 sm:ml-3" />
            </Link>
            <p className="mt-4 text-sm font-bold text-gray-500 uppercase">No technical skills needed</p>
          </div>
        </div>
      </section>

      {/* --- PROBLEM / SOLUTION --- */}
      <section className="py-16 sm:py-20 bg-white border-b-4 border-black px-4">
        <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="order-2 md:order-1">
                    <div className="neo-card p-6 bg-red-50 rotate-1">
                        <h3 className="text-xl font-black uppercase mb-4 text-red-600 flex items-center">
                            <SafeIcon icon={FiMessageSquare} className="mr-2"/> The Old Way
                        </h3>
                        <div className="space-y-3 font-mono text-sm text-gray-600">
                            <p className="bg-white p-2 border border-gray-300">"Wait, where did I save that email prompt?"</p>
                            <p className="bg-white p-2 border border-gray-300">"Let me scroll through 500 ChatGPT chats..."</p>
                            <p className="bg-white p-2 border border-gray-300">"I'll just type it out again from scratch."</p>
                        </div>
                    </div>
                </div>
                <div className="order-1 md:order-2">
                    <h2 className="text-4xl font-black uppercase mb-4">Never Lose a Great Idea</h2>
                    <p className="text-xl text-gray-700 leading-relaxed">
                        You know that feeling when you finally get ChatGPT to write exactly what you want? 
                        <br/><br/>
                        <span className="font-bold bg-neo-yellow px-1">Don't let that prompt disappear.</span> Save it in Prompts DB, give it a name, and use it forever.
                    </p>
                </div>
            </div>
        </div>
      </section>

      {/* --- HOW IT WORKS (Playful) --- */}
      <section className="py-16 sm:py-20 bg-neo-yellow border-b-4 border-black px-4">
        <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-4xl font-black uppercase mb-12">How It Works</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-white p-8 border-2 border-black shadow-neo relative">
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 bg-black text-white flex items-center justify-center font-black text-xl border-2 border-white rounded-full">1</div>
                    <SafeIcon icon={FiStar} size={40} className="mx-auto mb-4 text-neo-purple" />
                    <h3 className="text-xl font-bold uppercase mb-2">Save It</h3>
                    <p className="text-gray-600">Paste your best prompt. Give it a catchy title like "Professional Email Rewriter".</p>
                </div>

                <div className="bg-white p-8 border-2 border-black shadow-neo relative">
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 bg-black text-white flex items-center justify-center font-black text-xl border-2 border-white rounded-full">2</div>
                    <SafeIcon icon={FiEdit3} size={40} className="mx-auto mb-4 text-neo-blue" />
                    <h3 className="text-xl font-bold uppercase mb-2">Customize It</h3>
                    <p className="text-gray-600">Use <span className="font-mono bg-gray-100 px-1">{'{Topic}'}</span> placeholders. We'll ask you to fill them in each time you use it.</p>
                </div>

                <div className="bg-white p-8 border-2 border-black shadow-neo relative">
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 bg-black text-white flex items-center justify-center font-black text-xl border-2 border-white rounded-full">3</div>
                    <SafeIcon icon={FiCopy} size={40} className="mx-auto mb-4 text-neo-green" />
                    <h3 className="text-xl font-bold uppercase mb-2">Copy It</h3>
                    <p className="text-gray-600">Fill in the blanks, click "Copy", and paste perfectly formatted text into AI.</p>
                </div>
            </div>
        </div>
      </section>

      {/* --- SIMPLE FEATURES --- */}
      <section className="py-16 sm:py-24 bg-white border-b-4 border-black px-4">
        <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-black uppercase mb-10 text-center">Everything You Need</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                
                <div className="flex items-start p-4 hover:bg-gray-50 transition-colors">
                    <div className="bg-black text-white p-2 mr-4">
                        <SafeIcon icon={FiTag} size={20} />
                    </div>
                    <div>
                        <h4 className="font-bold text-lg uppercase">Simple Tags</h4>
                        <p className="text-sm text-gray-600 mt-1">Organize by "Work", "Personal", "Coding", or "Recipes". Find things fast.</p>
                    </div>
                </div>

                <div className="flex items-start p-4 hover:bg-gray-50 transition-colors">
                    <div className="bg-black text-white p-2 mr-4">
                        <SafeIcon icon={FiZap} size={20} />
                    </div>
                    <div>
                        <h4 className="font-bold text-lg uppercase">Instant Fill-in</h4>
                        <p className="text-sm text-gray-600 mt-1">Turn "Write a [Topic] poem" into a simple form. No more editing text manually.</p>
                    </div>
                </div>

                <div className="flex items-start p-4 hover:bg-gray-50 transition-colors">
                    <div className="bg-black text-white p-2 mr-4">
                        <SafeIcon icon={FiSmile} size={20} />
                    </div>
                    <div>
                        <h4 className="font-bold text-lg uppercase">Easy Interface</h4>
                        <p className="text-sm text-gray-600 mt-1">Big buttons, clear text, no confusing developer settings. It just works.</p>
                    </div>
                </div>

                <div className="flex items-start p-4 hover:bg-gray-50 transition-colors">
                    <div className="bg-black text-white p-2 mr-4">
                        <SafeIcon icon={FiBookOpen} size={20} />
                    </div>
                    <div>
                        <h4 className="font-bold text-lg uppercase">Private Library</h4>
                        <p className="text-sm text-gray-600 mt-1">Your prompts are stored locally in your browser. We don't spy on your ideas.</p>
                    </div>
                </div>

            </div>
        </div>
      </section>

      {/* --- FOOTER CTA --- */}
      <section className="py-16 sm:py-24 bg-black text-white px-4 text-center">
        <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-black uppercase mb-8">Ready to get organized?</h2>
            <Link to="/auth" className="inline-block px-8 py-4 sm:px-12 sm:py-6 bg-neo-green text-black border-2 border-white font-black text-lg sm:text-xl uppercase tracking-wider shadow-[8px_8px_0px_0px_#ffffff] hover:translate-x-[4px] hover:translate-y-[4px] hover:shadow-[4px_4px_0px_0px_#ffffff] transition-all min-h-[52px]">
                <span className="hidden sm:inline">Create Free Account</span>
                <span className="sm:hidden">Create Account</span>
            </Link>
        </div>
      </section>

      {/* --- SIMPLE FOOTER --- */}
      <footer className="bg-white py-8 px-4 border-t-4 border-black">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
                <SafeIcon icon={FiDatabase} size={20} />
                <span className="font-black text-lg uppercase">Prompts DB</span>
            </div>
            <div className="text-sm font-bold text-gray-500 uppercase">
                Made for Humans who use AI
            </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;